import React, { useEffect, useMemo } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import {
  getFirstBundleReportPath,
  REPORT_HUB_BUNDLE_CARDS,
  REPORT_HUB_PRESET_CARDS,
} from '@constants/Reports/reportsPage.constants'
import type { ReportsOutletContext } from '@pages/Reports/Reports.page'
import ReportHubCard from './ReportHubCard.component'

const ReportsHub: React.FC = (): React.ReactElement => {
  const { categoryId } = useParams<{ categoryId?: string }>()
  const navigate = useNavigate()
  const { searchQuery } = useOutletContext<ReportsOutletContext>()

  useEffect(() => {
    if (!categoryId) return
    const firstReportPath = getFirstBundleReportPath(categoryId)
    if (firstReportPath) navigate(firstReportPath, { replace: true })
  }, [categoryId, navigate])

  const filteredBundleCards = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return REPORT_HUB_BUNDLE_CARDS
    return REPORT_HUB_BUNDLE_CARDS.filter((card) =>
      card.title.toLowerCase().includes(q),
    )
  }, [searchQuery])

  return (
    <div className="reports-hub">
      <section
        className="reports-hub__section"
        aria-labelledby="reports-page-top-heading"
      >
        <div className="reports-hub__grid">
          {filteredBundleCards.map((card) => (
            <ReportHubCard
              key={card.id}
              domId={`report-hub-card-${card.id}`}
              title={card.title}
              description={card.description}
              footerLabel={card.footerLabel}
              variant={card.variant === 'create' ? 'create' : 'bundle'}
              onFooterClick={
                card.variant === 'create'
                  ? () => navigate('/report/custom')
                  : () =>
                      navigate(
                        getFirstBundleReportPath(card.id) ??
                          `/report/bundles/${card.id}`,
                      )
              }
            />
          ))}
        </div>
      </section>

      <section
        className="reports-hub__section reports-hub__section--second"
        aria-labelledby="reports-hub-presets-heading"
      >
        <h2
          id="reports-hub-presets-heading"
          className="reports-hub__section-title"
        >
          Report presets and automation
        </h2>
        <div className="reports-hub__grid reports-hub__grid--presets">
          {REPORT_HUB_PRESET_CARDS.map((card) => (
            <ReportHubCard
              key={card.id}
              title={card.title}
              description={card.description}
              footerLabel={card.footerLabel}
              variant="preset"
            />
          ))}
        </div>
      </section>
    </div>
  )
}

export default ReportsHub
